import type { PartNode, ShoppingCartItem } from '@partful/shared-types';
import { ChangeEvent, useCallback } from 'react';
// import PartThumbnail from 'src/components/PartThumbnail';
import { useTranslation } from 'react-i18next';
import { useTenant } from 'src/providers/Tenant';
import useFormattedPrice from 'src/services/formatCurrency';
import { GA } from 'src/services/gtag';
import { calculateTotalWithPOR } from 'src/services/isPOR';
import { QuantityCounter } from './QuantityCounter';
import {
  ContentContainer,
  DeleteButton,
  OneLineEllipsis,
  PaddingTop,
  PositionedCard,
  PriceTag,
  ProductInfo,
  StyledCardHeader,
  ThumbnailStyles,
  TwoLineEllipsis,
} from './styles';

interface CartItemProps extends ShoppingCartItem {
  removeItem: () => void;
  updateItemQuantity: (quantity: number) => void;
}

export default function MobilePanelCartItem({
  partName,
  quantity,
  partNumber,
  unitPrice,
  removeItem,
  updateItemQuantity,
}: CartItemProps) {
  const price = calculateTotalWithPOR([{ quantity, unitPrice }]);
  const { t } = useTranslation('common');
  const tenant = useTenant();

  const updateWithInputField = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value === '0' ? /* istanbul ignore next */ '1' : e.target.value;
      updateItemQuantity(parseInt(value));
    },
    [updateItemQuantity]
  );
  /* istanbul ignore next */
  const validateValue = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.target.value) {
      return;
    }
    updateItemQuantity(1);
  };

  const removeItemFromCart = useCallback(() => {
    removeItem();
    const part: PartNode = {
      partNumber,
      name: partName,
      price: unitPrice,
      children: [],
      id: partNumber,
    };
    GA.Ecommerce.removeItemFromCart(tenant, [part], quantity);
  }, [partName, partNumber, quantity, removeItem, tenant, unitPrice]);

  const removeSingleItemFromCart = useCallback(() => {
    updateItemQuantity(quantity ? quantity - 1 : /* istanbul ignore next */ 1);
    const part: PartNode = {
      partNumber,
      name: partName,
      price: unitPrice,
      children: [],
      id: partNumber,
    };
    GA.Ecommerce.removeItemFromCart(tenant, [part], 1);
  }, [partName, partNumber, quantity, tenant, unitPrice, updateItemQuantity]);

  const addSingleItemToCart = useCallback(() => {
    updateItemQuantity(quantity + 1);
    const part: PartNode = {
      partNumber,
      name: partName,
      price: unitPrice,
      children: [],
      id: partNumber,
    };
    GA.Ecommerce.addItemToCart(tenant, [part], 1);
  }, [partName, partNumber, quantity, tenant, unitPrice, updateItemQuantity]);

  const updateCartItems = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      updateWithInputField(e);
      const part: PartNode = {
        partNumber,
        name: partName,
        price: unitPrice,
        children: [],
        id: partNumber,
      };
      if (+e.target.value < quantity) {
        GA.Ecommerce.removeItemFromCart(tenant, [part], quantity - +e.target.value);
      } else {
        GA.Ecommerce.addItemToCart(tenant, [part], +e.target.value - quantity);
      }
    },
    [partName, partNumber, quantity, tenant, unitPrice, updateWithInputField]
  );

  return (
    <PositionedCard data-testid="shopping-cart-panel-item">
      <StyledCardHeader>
        <ThumbnailStyles>
          {/* <PartThumbnail partNumber={partNumber} quantity={quantity} height={100} width={70} transparent />remove for now SVT-2407 */}
        </ThumbnailStyles>
        <ProductInfo>
          <TwoLineEllipsis>
            <strong>{partName}</strong>
          </TwoLineEllipsis>
          <OneLineEllipsis>{partNumber}</OneLineEllipsis>
        </ProductInfo>
      </StyledCardHeader>
      <ContentContainer>
        <PaddingTop>
          <DeleteButton onClick={removeItemFromCart}>{t('delete')}</DeleteButton>
          <QuantityCounter
            quantity={quantity}
            addOneMore={addSingleItemToCart}
            removeOneLess={removeSingleItemFromCart}
            removeItem={removeItemFromCart}
            updateWithInputField={updateCartItems}
            validateValue={validateValue}
          />
          <PriceTag data-testid="cart-item-panel-price">
            {useFormattedPrice({ price, shortLabel: true, smallFont: true })}
          </PriceTag>
        </PaddingTop>
      </ContentContainer>
    </PositionedCard>
  );
}
